/**
 * AI Prediction data models.
 *
 * These types represent the prediction data returned by the AI Predictions API
 * (https://api.hydry.de) and stored in Firestore under `comboxes/{serialNo}/chains/{chainId}`.
 *
 * Reference: ml-predictor-reference/frontend-api-reference.md
 */
import { firestore } from "firebase/app";
import {
  COLOR_AI_PREDICTION_GOOD,
  COLOR_AI_PREDICTION_NEEDS_REVIEW,
  COLOR_AI_PREDICTION_CRITICAL,
  COLOR_AI_PREDICTION_BAR_TRACK,
} from "@/utils/colors";

// ─── Traffic Light ────────────────────────────────────────────────────────────

/**
 * Traffic light classification for AI prediction quality assessment.
 *
 * - `GOOD` — Drying progressing normally
 * - `NEEDS_REVIEW` — Potential issues detected, review recommended
 * - `CRITICAL` — Significant issues detected, immediate review needed
 * - `UNKNOWN` — No AI data available
 */
export type TrafficLight = "GOOD" | "NEEDS_REVIEW" | "CRITICAL" | "UNKNOWN";

/**
 * Source of the currently resolved prediction result.
 */
export type PredictionSource = "daily" | "correction" | "manual";

/**
 * Trigger type for a prediction execution.
 */
export type PredictionTrigger = "daily" | "manual";

/**
 * Valid window values for prediction time slices.
 */
export type PredictionWindow = "all" | "3" | "6" | "9" | "12";

// ─── Binary Predictions ──────────────────────────────────────────────────────

/**
 * Binary classification predictions (Normal vs. Abnormal).
 *
 * Note: Keys may vary in casing between endpoints. See `normalizePredictionKeys()`.
 */
export interface BinaryPrediction {
  abnormal: number;
  normal: number;
}

/**
 * Dry/not-dry prediction.
 *
 * Note: Keys are capitalized strings ("True"/"False"), not booleans.
 */
export interface DryPrediction {
  True: number;
  False: number;
}

// ─── Group Predictions ───────────────────────────────────────────────────────

/**
 * Group A (Hauptlabels) — multi-class prediction for the primary drying issue.
 */
export interface GroupAPrediction {
  normal: number;
  powerOutages: number;
  newMoistureIngress: number;
  slowDrying: number;
  unfavorableAmbientConditions: number;
}

/**
 * Group D (Ursache langsame Trocknung) — multi-class prediction for root cause
 * of slow drying.
 *
 * Note: `lowPower` was removed in schema v2 (4 classes instead of 5).
 */
export interface GroupDPrediction {
  peripheralMoisture: number;
  withoutFoil: number;
  ambientConditions: number;
  lowAirflow: number;
}

// ─── Prediction Entry (v2 Schema) ────────────────────────────────────────────

/**
 * Model confidence level for a prediction target.
 *
 * - `high` — margin from threshold ≥ 0.20
 * - `medium` — margin from threshold ≥ 0.10
 * - `low` — margin from threshold < 0.10
 */
export type ConfidenceLevel = "high" | "medium" | "low" | "none";

/**
 * A single prediction target entry in schema v2.
 *
 * In v1, each target in `predictions` was a flat probability dict
 * (e.g., `{ abnormal: 0.9, normal: 0.1 }`). In v2, each target is wrapped
 * in this structure with additional metadata.
 */
export interface PredictionEntry {
  /** Precomputed winning class. String for multi-class, boolean for binary targets. */
  predictedClass: string | boolean;
  /** Model confidence level based on margin from threshold. */
  confidence: ConfidenceLevel;
  /** Probability distribution over class labels. */
  probabilities: Record<string, number>;
  /** Decision threshold used (number for binary, "argmax" for multi-class). */
  threshold: number | "argmax";
  /** Algorithm used (e.g., "catboost", "xgb", "cnn", "fusion"). */
  modelType: string;
  /** Pipeline that produced this result (e.g., "tabular", "cnn", "fusion"). */
  source: string;
}

// ─── Per-Window Result ───────────────────────────────────────────────────────

/**
 * All predictions for a single time window (v2 schema).
 *
 * Each target is a `PredictionEntry` containing probabilities, predicted class,
 * confidence, and model metadata. Use `.probabilities` to access the probability
 * distribution, and `.predictedClass` for the precomputed winning class.
 */
export interface WindowPredictions {
  binary: PredictionEntry;
  dry: PredictionEntry;
  groupA: PredictionEntry;
  groupD: PredictionEntry;
  qualityScore?: PredictionEntry;
  lowInitialMoisture?: PredictionEntry;
  newMoistureIngress?: PredictionEntry;
  powerOutage?: PredictionEntry;
  relayDefect?: PredictionEntry;
  slowDrying?: PredictionEntry;
  stationary?: PredictionEntry;
  unfavorableAmbient?: PredictionEntry;
}

/**
 * Result for a single time window within a prediction execution.
 */
export interface WindowResult {
  chainId: string;
  window: number | string;
  trafficLight: TrafficLight;
  isDry: boolean;
  isStationary: boolean;
  predictions: WindowPredictions;
  /** Top predicted class per key model (v2). */
  topClass?: { groupA: string; binary: string };
  /** Top probability per key model (v2). */
  topProbability?: { groupA: number; binary: number };
  /** P(Good) from the quality model — drives traffic light (v2). */
  qualityScore?: number;
  /** Which pipeline produced the quality score (v2). */
  qualityScoreSource?: string;
  imageGenMs?: number;
  predictionMs?: number;
  tabularPredictionMs?: number;
  fusionPredictionMs?: number;
  modelSet?: string;
}

/**
 * Aggregate (final) result across all windows.
 */
export interface FinalResult {
  chainId: string;
  finalTrafficLight: TrafficLight;
  windowsProcessed?: number;
  modelLoadMs?: number;
  isDry?: boolean;
  isStationary?: boolean;
}

// ─── API Response Types ──────────────────────────────────────────────────────

/**
 * Response from `POST /predict/manual`.
 */
export interface ManualPredictResponse {
  chainId: string;
  serialNo: string;
  executionId: string;
  trigger: "manual";
  persisted: boolean;
  predictMs: number;
  windows: WindowResult[];
  final: FinalResult;
}

/**
 * Daily prediction document (from `GET /predictions/{chainId}`).
 *
 * `results.windows` is a **list** (array) of window objects.
 */
export interface DailyPrediction {
  executionId: string;
  trigger: "daily";
  createdAt: string;
  modelSet: string;
  /** RENO model version (e.g. "R1.0"). */
  modelVersion?: string;
  /** Pre-aggregated quality score (0–1) from the "all" window. */
  qualityScore?: number;
  /** Pre-aggregated per-target confidence from the "all" window. */
  targetConfidence?: Record<string, ConfidenceLevel>;
  results: {
    windows: WindowResult[];
    final: FinalResult;
  };
}

/**
 * Manual prediction document (from `GET /predictions/{chainId}`).
 *
 * `results.windows` is a **map** keyed by window string ("3", "all", etc.),
 * NOT a list. This is the most critical format difference between daily and manual.
 */
export interface ManualPrediction {
  executionId: string;
  trigger: "manual";
  /** RENO model version (e.g. "R1.0"). */
  modelVersion?: string;
  /** Pre-aggregated quality score (0–1) from the "all" window. */
  qualityScore?: number;
  /** Pre-aggregated per-target confidence from the "all" window. */
  targetConfidence?: Record<string, ConfidenceLevel>;
  results: {
    windows: Record<string, WindowResult>;
    final: FinalResult;
  };
}

// ─── Corrections ─────────────────────────────────────────────────────────────

/**
 * Manual-only labels that have no AI model prediction.
 * These appear as plain checkboxes in the Quality page (Row 4, Sub-groups A-C).
 */
export interface ManualLabels {
  ambientHumidityBad?: boolean;
  notForTraining?: boolean;
  clearClass?: boolean;
}

/**
 * Binary label corrections.
 */
export interface BinaryLabelCorrections {
  binary?: string | boolean;
  lowInitialMoisture?: boolean;
  slowDrying?: boolean;
  newMoisture?: boolean;
  powerFailure?: boolean;
  relayDefect?: boolean;
  unfavorableConditions?: boolean;
}

/**
 * Valid values for groupA correction.
 */
export type GroupAClass =
  | "Normal"
  | "Power Failures"
  | "New Moisture"
  | "Slow Drying"
  | "Unfavorable Conditions";

/**
 * Valid values for groupD correction.
 *
 * Note: `"Low Power"` was removed in schema v2.
 */
export type GroupDClass =
  | "Peripheral Moisture"
  | "Without Foil"
  | "Ambient Conditions"
  | "Low Airflow";

/**
 * Timeline marker value: relative position and corresponding timestamp.
 */
export interface TimelineMarkerPoint {
  relativeValue: number;
  timestamp: firestore.Timestamp;
}

/**
 * Correction payload for `POST /corrections`.
 */
export interface CorrectionPayload {
  trafficLight?: TrafficLight;
  isDry?: boolean;
  isStationary?: boolean;
  groupA?: GroupAClass | null;
  groupD?: GroupDClass | null;
  binaryLabels?: BinaryLabelCorrections;
  manualLabels?: ManualLabels;
  stationaryAt?: TimelineMarkerPoint | null;
  dryAt?: TimelineMarkerPoint | null;
}

/**
 * Request body for `POST /corrections`.
 */
export interface SaveCorrectionRequest {
  serialNo: string;
  chainId: string;
  window: PredictionWindow;
  userId: string;
  sourceExecutionId: string;
  sourceTrigger: PredictionTrigger;
  correction: CorrectionPayload;
}

/**
 * Response from `POST /corrections`.
 */
export interface SaveCorrectionResponse {
  status: "saved";
  correctedAt: string;
  chainId: string;
  window: string;
}

/**
 * Single window correction entry (from `GET /corrections/{chainId}` or
 * the correction field of `GET /predictions/{chainId}`).
 */
export interface WindowCorrection {
  trafficLight?: TrafficLight;
  isDry?: boolean;
  isStationary?: boolean;
  window?: string;
  correctedAt: string;
  correctedBy: string;
  sourceExecutionId: string;
  sourceTrigger: PredictionTrigger;
  modelSet?: string;
  groupA?: GroupAClass | null;
  groupD?: GroupDClass | null;
  binaryLabels?: BinaryLabelCorrections;
  manualLabels?: ManualLabels;
  stationaryAt?: TimelineMarkerPoint | null;
  dryAt?: TimelineMarkerPoint | null;
}

/**
 * Correction document structure.
 */
export interface CorrectionDocument {
  corrections: Record<string, WindowCorrection>;
  finalResult?: WindowCorrection;
}

// ─── Predictions Response (Quality Page) ─────────────────────────────────────

/**
 * Daily summary within the chain summary.
 */
export interface DailySummary {
  trafficLight: TrafficLight;
  isDry: boolean;
  isStationary: boolean;
  latestExecutionId: string;
  source: "daily";
}

/**
 * Resolved latest summary — the "winner" between daily and correction.
 */
export interface LatestSummary {
  trafficLight: TrafficLight;
  source: PredictionSource;
  /** RENO model version of the resolved latest result. */
  modelVersion?: string;
}

/**
 * Full response from `GET /predictions/{chainId}?serialNo={serialNo}`.
 *
 * This is the primary payload for the Quality page — one call returns
 * daily result, manual result, correction, and chain summary.
 */
export interface PredictionsResponse {
  chainId: string;
  serialNo: string;
  daily: DailyPrediction | null;
  manual: ManualPrediction | null;
  correction: CorrectionDocument | null;
  summary: {
    daily: DailySummary;
    latest: LatestSummary;
    correction?: WindowCorrection;
  };
  /** All distinct RENO model versions with stored results for this chain. */
  availableModelVersions?: string[];
  /** The current model version running on the server (e.g. "R1.0"). */
  latestAvailableModel?: string;
}

// ─── Trend ───────────────────────────────────────────────────────────────────

/**
 * Single entry in the trend history.
 */
export interface TrendEntry {
  executionId: string;
  date: string;
  createdAt: string;
  trafficLight: TrafficLight;
  isDry: boolean;
  isStationary: boolean;
  modelSet: string;
  /** RENO model version (e.g. "R1.0"). May be "unknown" for pre-deploy data. */
  modelVersion?: string;
  /** Quality score (0–1) for trend chart. Only present for entries after 2026-05-12. */
  qualityScore?: number;
  /** Per-target confidence levels for trend breakdown. */
  targetConfidence?: Record<string, ConfidenceLevel>;
}

/**
 * Response from `GET /predictions/{chainId}/trend`.
 */
export interface TrendResponse {
  chainId: string;
  serialNo: string;
  count: number;
  trend: TrendEntry[];
  /**
   * The server's currently deployed model version ("latest"). Present so a
   * read-only view can gate strictly on the latest model (e.g. the device
   * Details graph shows the trend only when the latest model has data).
   */
  latestAvailableModel?: string;
}

/**
 * Resolve the "latest" model version to gate the read-only Details view on.
 *
 * Prefers the backend-provided `latestAvailableModel` (the deployed model). When
 * that is absent — e.g. an older backend that predates the `/trend`
 * `latestAvailableModel` field, or an Office deploy that shipped ahead of the
 * Assist deploy — it falls back to the model version that produced the most
 * recent trend entry (the current model writes the newest daily docs). This
 * keeps the Details view working across frontend/backend version skew instead of
 * silently hiding. Returns `undefined` only when there is no usable signal.
 */
export function resolveLatestModelVersion(
  entries: TrendEntry[],
  latestAvailableModel: string | undefined
): string | undefined {
  const explicit = latestAvailableModel?.trim();
  if (explicit) return explicit;

  let newest: TrendEntry | undefined;
  for (const entry of entries) {
    if (!entry.modelVersion) continue;
    if (!newest || new Date(entry.date).getTime() > new Date(newest.date).getTime()) {
      newest = entry;
    }
  }
  return newest?.modelVersion?.trim() || undefined;
}

/**
 * Filter trend entries to one model version: the latest for the read-only
 * Details view, the selected one for the Quality view. Returns `[]` when no
 * version is known, so the view hides rather than showing another model's days.
 */
export function filterTrendEntriesToModelVersion(
  entries: TrendEntry[],
  modelVersion: string | undefined
): TrendEntry[] {
  const version = modelVersion?.trim();
  if (!version) return [];
  return entries.filter((entry) => (entry.modelVersion ?? "") === version);
}

// ─── Manual Prediction Request ───────────────────────────────────────────────

/**
 * Request body for `POST /predict/manual`.
 */
export interface ManualPredictRequest {
  serialNo: string;
  chainId?: string;
  windows?: (number | "all")[];
  dryRun?: boolean;
}

// ─── Backfill ────────────────────────────────────────────────────────────────

/**
 * Request body for `POST /predict/backfill`.
 * Generates predictions for all historical days of a drying chain.
 */
export interface BackfillRequest {
  serialNo: string;
  chainId: string;
}

/** Activation scope for client-level ASSIST backfill orchestration. */
export type ActivationBackfillScope = "ALL_DRYINGS" | "ACTIVE_AND_NEW_ONLY";

/** Source that initiated activation backfill orchestration. */
export type ActivationBackfillTriggerSource = "system_activation" | "manual_retry";

/** Request body for `POST /predict/activation-backfill`. */
export interface ActivationBackfillRequest {
  clientId: string;
  scope?: ActivationBackfillScope;
  triggerSource?: ActivationBackfillTriggerSource;
}

/** Status values returned by `POST /predict/activation-backfill`. */
export type ActivationBackfillStatus = "started" | "already_running" | "no_op";

/** Response from `POST /predict/activation-backfill`. */
export interface ActivationBackfillResponse {
  accepted: boolean;
  jobId?: string;
  status: ActivationBackfillStatus;
  scope: ActivationBackfillScope;
  clientId: string;
}

/** Backfill job status values. */
export type BackfillStatus = "running" | "complete" | "partial" | "failed";

/**
 * Response from `POST /predict/backfill` (202) and `GET /predict/backfill/{jobId}`.
 */
export interface BackfillResponse {
  jobId: string;
  chainId: string;
  serialNo: string;
  modelVersion: string;
  status: BackfillStatus;
  progress: number;
  totalDays: number;
  daysProcessed: number;
  daysSkipped: number;
  errors: number;
  startedAt?: string;
  completedAt?: string;
}

// ─── Firestore Direct Read (Device List) ─────────────────────────────────────

/**
 * The `aiPrediction.latest` field on a chain document, used for the
 * traffic-light border on the device list (Phase 6).
 *
 * This is read directly from Firestore — no API call needed.
 * Path: `comboxes/{serialNo}/chains/{chainId}.aiPrediction.latest`
 */
export interface AiPredictionLatest {
  trafficLight: TrafficLight;
  isDry: boolean;
  isStationary: boolean;
  window: string;
  source: PredictionSource;
  latestExecutionId: string;
  updatedAt: firestore.Timestamp;
  modelSet: string;
  schemaVersion: string;
  /** RENO model version (e.g. "R1.0"). */
  modelVersion?: string;
  /** Top predicted class per key model (v2). */
  topClass?: { groupA: string; binary: string };
  /** Top probability per key model (v2). */
  topProbability?: { groupA: number; binary: number };
  /** P(Good) for latest result (0-1), used for gradient quality coloring. */
  qualityScore?: number;
}

/**
 * The `aiPrediction` field on a chain document (full structure).
 */
export interface AiPredictionField {
  daily?: DailySummary;
  latest?: AiPredictionLatest;
  correction?: WindowCorrection;
}

// ─── Error Response ──────────────────────────────────────────────────────────

/**
 * Standard error response from the API.
 */
export interface AiApiErrorResponse {
  error: string;
}

// ─── Label Mapping Helpers ───────────────────────────────────────────────────

/**
 * Maps traffic light values to German UI labels.
 */
export const TRAFFIC_LIGHT_LABELS: Record<TrafficLight, string> = {
  GOOD: "Gut",
  NEEDS_REVIEW: "Prüfen",
  CRITICAL: "Kritisch",
  UNKNOWN: "Unbekannt",
};

/**
 * Maps groupA prediction keys to German UI labels.
 */
export const GROUP_A_LABELS: Record<string, string> = {
  normal: "Normal",
  powerOutages: "Stromausfälle",
  newMoistureIngress: "Neuer Feuchteeintrag",
  slowDrying: "Langsame Trocknung",
  unfavorableAmbientConditions: "Umgebungsbedingung ungünstig",
};

/**
 * Maps groupD prediction keys to German UI labels.
 */
export const GROUP_D_LABELS: Record<string, string> = {
  peripheralMoisture: "Feuchte in Peripherie",
  withoutFoil: "Ohne Folie",
  ambientConditions: "Umgebungsbedingungen",
  lowAirflow: "Strömung gering",
};

/**
 * Maps binary label keys to i18n translation keys.
 */
export const BINARY_LABEL_UI_LABELS: Record<string, string> = {
  powerFailure: "quality.labels.powerFailure",
  newMoisture: "quality.labels.newMoisture",
  slowDrying: "quality.labels.slowDrying",
  unfavorableConditions: "quality.labels.unfavorableConditions",
  lowInitialMoisture: "quality.labels.lowInitialMoisture",
  relayDefect: "quality.labels.relayDefect",
};

/**
 * Maps manual-only label keys to i18n translation keys.
 */
export const MANUAL_LABEL_UI_LABELS: Record<string, string> = {
  ambientHumidityBad: "quality.labels.ambientHumidityBad",
  notForTraining: "quality.labels.notForTraining",
  clearClass: "quality.labels.clearClass",
};

/**
 * Maps groupA correction class names to prediction keys.
 */
export const GROUP_A_CLASS_TO_KEY: Record<GroupAClass, string> = {
  "Normal": "normal",
  "Power Failures": "powerOutages",
  "New Moisture": "newMoistureIngress",
  "Slow Drying": "slowDrying",
  "Unfavorable Conditions": "unfavorableAmbientConditions",
};

/**
 * Maps groupD correction class names to prediction keys.
 */
export const GROUP_D_CLASS_TO_KEY: Record<GroupDClass, string> = {
  "Peripheral Moisture": "peripheralMoisture",
  "Without Foil": "withoutFoil",
  "Ambient Conditions": "ambientConditions",
  "Low Airflow": "lowAirflow",
};

/**
 * Maps v2 prediction target keys to correction binaryLabel keys.
 *
 * The prediction output uses v2 keys (e.g., `powerOutage`) but the
 * correction endpoint still expects v1 keys (e.g., `powerFailure`).
 */
export const PREDICTION_TO_CORRECTION_KEY: Record<string, string> = {
  powerOutage: "powerFailure",
  newMoistureIngress: "newMoisture",
  unfavorableAmbient: "unfavorableConditions",
  // These are the same in both:
  slowDrying: "slowDrying",
  lowInitialMoisture: "lowInitialMoisture",
  relayDefect: "relayDefect",
  binary: "binary",
};

// ─── Utility ─────────────────────────────────────────────────────────────────

/**
 * Returns the CSS color for a traffic light value.
 */
export function getTrafficLightColor(trafficLight: TrafficLight): string {
  switch (trafficLight) {
    case "GOOD":
      return COLOR_AI_PREDICTION_GOOD;
    case "NEEDS_REVIEW":
      return COLOR_AI_PREDICTION_NEEDS_REVIEW;
    case "CRITICAL":
      return COLOR_AI_PREDICTION_CRITICAL;
    case "UNKNOWN":
    default:
      return "transparent";
  }
}

/**
 * Returns the border style string for a traffic light value.
 * Used by the device list (Phase 6) for mini-verlauf borders.
 */
export function getTrafficLightBorderStyle(trafficLight: TrafficLight): string {
  if (trafficLight === "UNKNOWN") {
    return "";
  }
  return `border: 3px solid ${getTrafficLightColor(trafficLight)}`;
}

function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const normalized = hex.trim();
  const match = /^#?([\da-fA-F]{2})([\da-fA-F]{2})([\da-fA-F]{2})$/.exec(normalized);
  if (!match) {
    return null;
  }

  return {
    r: parseInt(match[1], 16),
    g: parseInt(match[2], 16),
    b: parseInt(match[3], 16),
  };
}

function interpolateHexColors(startHex: string, endHex: string, t: number): string {
  const start = hexToRgb(startHex);
  const end = hexToRgb(endHex);
  if (!start || !end) {
    return endHex;
  }

  const clampedT = Math.max(0, Math.min(1, t));
  const r = Math.round(start.r + (end.r - start.r) * clampedT);
  const g = Math.round(start.g + (end.g - start.g) * clampedT);
  const b = Math.round(start.b + (end.b - start.b) * clampedT);

  return `rgb(${r},${g},${b})`;
}

/**
 * Continuous quality-score color interpolation:
 * CRITICAL (0.0) -> NEEDS_REVIEW (0.5) -> GOOD (1.0).
 */
export function getQualityScoreColor(score: number | undefined | null): string {
  if (score == null) {
    return COLOR_AI_PREDICTION_BAR_TRACK;
  }

  const s = Math.max(0, Math.min(1, score));

  if (s <= 0.5) {
    return interpolateHexColors(
      COLOR_AI_PREDICTION_CRITICAL,
      COLOR_AI_PREDICTION_NEEDS_REVIEW,
      s * 2
    );
  }

  return interpolateHexColors(
    COLOR_AI_PREDICTION_NEEDS_REVIEW,
    COLOR_AI_PREDICTION_GOOD,
    (s - 0.5) * 2
  );
}

/**
 * Returns the border style string for a quality score value.
 */
export function getQualityScoreBorderStyle(
  score: number | undefined | null
): string {
  if (score == null) {
    return "";
  }
  return `border: 3px solid ${getQualityScoreColor(score)}`;
}

/**
 * Normalizes prediction keys to lowercase for consistent comparison.
 *
 * The API may return keys in varying cases (e.g., "Anormal" vs "abnormal",
 * "True" vs "true"). This function normalizes a predictions object.
 */
export function normalizePredictionKeys<T>(obj: Record<string, T> | null | undefined): Record<string, T> {
  if (!obj) return {} as Record<string, T>;
  const normalized: Record<string, T> = {};
  for (const key of Object.keys(obj)) {
    normalized[key.toLowerCase()] = obj[key];
  }
  return normalized;
}

/**
 * Extracts the probability of the "True" class from a prediction.
 *
 * Accepts either a v2 `PredictionEntry` (unwraps `.probabilities`) or a legacy
 * flat dict with "True"/"False" keys.
 */
export function getTrueProbability(prediction: PredictionEntry | DryPrediction | Record<string, number> | null | undefined): number {
  if (!prediction) return 0;
  const probs = (prediction as PredictionEntry).probabilities ?? prediction;
  const normalized = normalizePredictionKeys(probs as Record<string, number>);
  return normalized["true"] ?? 0;
}

/**
 * Determines whether a boolean-style prediction is considered "true".
 *
 * Uses `predictedClass` (boolean) from v2 PredictionEntry when available,
 * otherwise falls back to probability threshold (≥ 50%).
 */
export function isBooleanPredictionTrue(prediction: PredictionEntry | DryPrediction | Record<string, number> | null | undefined): boolean {
  if (!prediction) return false;
  const entry = prediction as PredictionEntry;
  if (typeof entry.predictedClass === "boolean") {
    return entry.predictedClass;
  }
  return getTrueProbability(prediction) >= 0.5;
}

/**
 * Returns the highest probability class from a prediction.
 *
 * Accepts either a v2 `PredictionEntry` (unwraps `.probabilities`) or a legacy
 * flat probability dict.
 */
export function getHighestProbabilityClass(prediction: PredictionEntry | Record<string, number>): { key: string; probability: number } {
  const probs: Record<string, number> = (prediction as PredictionEntry).probabilities ?? (prediction as Record<string, number>);
  let maxKey = "";
  let maxProb = -1;
  for (const [key, prob] of Object.entries(probs)) {
    if (prob > maxProb) {
      maxKey = key;
      maxProb = prob;
    }
  }
  return { key: maxKey, probability: maxProb };
}

/**
 * Extracts window results from either daily (list) or manual (map) format
 * into a uniform list.
 */
export function extractWindowResults(
  windows: WindowResult[] | Record<string, WindowResult>
): WindowResult[] {
  if (Array.isArray(windows)) {
    return windows;
  }
  return Object.values(windows);
}

// ─── Schema v1 → v2 Adapter ─────────────────────────────────────────────────

/**
 * Wraps a flat probability dict into a PredictionEntry for multi-class targets.
 * Used by the v1→v2 normalizer.
 */
function wrapMultiClass(probs: Record<string, number> | undefined): PredictionEntry | undefined {
  if (!probs) return undefined;
  const entries = Object.entries(probs);
  if (entries.length === 0) return undefined;
  const top = entries.reduce((a, b) => (a[1] > b[1] ? a : b));
  return {
    predictedClass: top[0],
    confidence: "high",
    probabilities: probs,
    threshold: "argmax",
    modelType: "unknown",
    source: "unknown",
  };
}

/**
 * Wraps a flat probability dict into a PredictionEntry for boolean targets.
 * Boolean targets use string keys "True"/"False" in probabilities but
 * native booleans for predictedClass.
 */
function wrapBoolean(probs: Record<string, number> | undefined): PredictionEntry | undefined {
  if (!probs) return undefined;
  const normalized = normalizePredictionKeys(probs);
  const trueProb = normalized["true"] ?? 0;
  return {
    predictedClass: trueProb >= 0.5,
    confidence: "high",
    probabilities: probs,
    threshold: 0.5,
    modelType: "unknown",
    source: "unknown",
  };
}

/**
 * Whether a raw predictions map is already in v2 form.
 *
 * v2 wraps every target in a `PredictionEntry` (which carries `.probabilities`),
 * while v1 maps each target straight to a flat probability dict. Detection looks
 * at *any* target rather than at `binary` specifically: only the EDT program
 * group emits a `binary` model, so keying off it misread the genuinely-v2
 * payloads of the monitoring (program 3) and raumtrocknung (program 8) groups as
 * v1 and re-wrapped their entries, zeroing out every probability.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function isV2Predictions(predictions: any): boolean {
  if (!predictions || typeof predictions !== "object") return false;
  return Object.values(predictions).some(
    (entry) =>
      entry !== null &&
      typeof entry === "object" &&
      "probabilities" in (entry as Record<string, unknown>)
  );
}

/**
 * Normalizes a WindowPredictions object from v1 (flat probability dicts) to v2
 * (PredictionEntry with nested `.probabilities`).
 *
 * If already v2, returns as-is. If v1, wraps each target in a PredictionEntry and
 * maps renamed keys (e.g., `stationear` → `stationary`).
 *
 * @param predictions - Raw predictions object from the API (v1 or v2)
 * @returns Normalized v2 WindowPredictions
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function normalizeWindowPredictions(predictions: any): WindowPredictions {
  if (isV2Predictions(predictions)) {
    return predictions as WindowPredictions;
  }

  // v1 format — wrap each target and map renamed keys
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const v1 = predictions as any;

  return {
    binary: wrapMultiClass(v1.binary) as PredictionEntry,
    dry: wrapBoolean(v1.dry) as PredictionEntry,
    groupA: wrapMultiClass(v1.groupA) as PredictionEntry,
    groupD: wrapMultiClass(v1.groupD) as PredictionEntry,
    qualityScore: wrapMultiClass(v1.qualityScore),
    lowInitialMoisture: wrapBoolean(v1.lowInitialMoisture),
    newMoistureIngress: wrapBoolean(v1.newMoistureIngress ?? v1.newMoisture),
    powerOutage: wrapBoolean(v1.powerOutage ?? v1.powerFailure),
    relayDefect: wrapBoolean(v1.relayDefect),
    slowDrying: wrapBoolean(v1.slowDrying),
    stationary: wrapBoolean(v1.stationary ?? v1.stationear),
    unfavorableAmbient: wrapBoolean(v1.unfavorableAmbient ?? v1.unfavorableConditions),
  };
}
